import { useCallback, useDeferredValue, useEffect, useState } from 'react';

import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { type DropdownOption } from '@pkg/components';
import { useAdminCollectionSearch } from '@pkg/hooks';
import { getNextPageParam, getPagesPagingParams } from '@pkg/hooks/deprecated';
import { type SupportedNetworkName } from '@pkg/types';

import {
    type CompetitionCollection,
    type CompetitionCollectionPatchParams,
    CompetitionCollectionStatus,
    getChainFromIndex,
    type SortingDir,
} from '@app/client';
import {
    callAdminGetCompetitionCollections,
    callUpdateCompetitionCollection,
} from '@app/client/resources/admin/fantasy';
import { callGetCompetitionsCollections } from '@app/client/resources/competitions';
import { ADMIN_COMPETITION_COLLECTION_CACHE, COMPETITION_COLLECTION_CACHE } from '@app/client/resources/fantasy';
import { useFantasyCollectionValidityStore } from '@app/store/zustand';

export function useGetAdminCompetitionCollections(
    slugQuery?: string,
    statusQuery?: DropdownOption,
    sortDirectionQuery?: SortingDir,
) {
    const [competitionCollections, setCompetitionCollections] = useState<CompetitionCollection[]>([]);
    const [searchResults, setSearchResults] = useState<CompetitionCollection[]>([]);

    const cache = [ADMIN_COMPETITION_COLLECTION_CACHE, slugQuery, statusQuery, sortDirectionQuery];

    const slugs = slugQuery ? { slugs: slugQuery } : {};
    const status = statusQuery?.value ? { status: statusQuery.value } : {};
    const dir = { dir: sortDirectionQuery ?? 'Asc' };
    const searchQueries = { ...slugs, ...status, ...dir };

    const {
        data: competitionCollectionsData,
        isLoading,
        isFetching,
        isFetched,
        ...rest
    } = useInfiniteQuery({
        queryKey: cache,
        queryFn: ({ pageParam = 0, signal }) => {
            const pageQuery = { page: pageParam || 0, limit: 20, sort: 'slug' };
            const query = { ...searchQueries, ...pageQuery };
            return callAdminGetCompetitionCollections({
                query,
                settings: { signal },
            });
        },
        retry: false,
        placeholderData: keepPreviousData,
        initialPageParam: 0,
        getNextPageParam,
    });

    const pagingParams = getPagesPagingParams(competitionCollectionsData?.pages);
    const collections = competitionCollectionsData?.pages[pagingParams?.page || 0].data;
    const deferredQuery = useDeferredValue(slugQuery);
    const gsQuery = isFetched && !collections?.length ? deferredQuery : undefined;

    const {
        data: collectionSearchData,
        isLoading: isLoadingGS,
        isFetching: isFetchingGS,
    } = useAdminCollectionSearch({
        query: gsQuery,
    });

    const findMatches = useCallback(() => {
        if (collectionSearchData && !collectionSearchData.length) {
            setSearchResults([]);
        }
        if (collectionSearchData?.length) {
            const formattedMatches = collectionSearchData.map((searchMatch) => {
                const { id, slug, chain } = searchMatch;
                return {
                    id,
                    slug,
                    created_at: new Date().toISOString(),
                    status: CompetitionCollectionStatus.Normal,
                    network: getChainFromIndex(chain) as SupportedNetworkName,
                };
            });
            if (formattedMatches?.length) {
                setSearchResults(formattedMatches);
                return;
            }
        }
        const collectionMatches = collections?.filter((col) => collectionSearchData?.find((c) => c.slug === col.slug));
        if (collectionMatches?.length) {
            setSearchResults(collectionMatches);
            return;
        }
    }, [collectionSearchData, collections]);

    useEffect(() => {
        if (isLoading || isFetching) return;
        if (!collections?.length && gsQuery?.length) {
            findMatches();
        }
        if (collections) {
            setCompetitionCollections(collections);
        }
    }, [collections, findMatches, gsQuery?.length, isFetching, isLoading]);

    useEffect(() => {
        if (!slugQuery?.length) {
            setSearchResults([]);
        }
    }, [slugQuery?.length]);

    return {
        competitionCollections: searchResults?.length ? searchResults : competitionCollections,
        pagingParams,
        isLoading,
        isFetching,
        isFetchingGS,
        isSearching: gsQuery ? isLoadingGS : false,
        ...rest,
    };
}

export function useUpdateCollectionStatus() {
    return useMutation({
        mutationFn: ({
            value,
            collectionSlug,
            chain,
        }: {
            value: CompetitionCollectionPatchParams;
            collectionSlug: string;
            chain: SupportedNetworkName;
        }) => callUpdateCompetitionCollection({ value, slug: collectionSlug, chain: chain }),
        onSuccess: (data) => {
            if (!data.ok && data.error) {
                alert(data.error);
            }
        },
        onError: (error) => {
            if (error) {
                alert(error);
            }
        },
    });
}

export const useGetCompetitionCollections = () => {
    const queryClient = useQueryClient();
    const { setCollectionsStatuses: setCollectionsStatusesStore } = useFantasyCollectionValidityStore();

    return async (slugs?: string[]) => {
        const customQueryString = slugs?.length ? `?slugs=${slugs?.slice(0, 20).join(',')}` : undefined;
        const cache = [COMPETITION_COLLECTION_CACHE, customQueryString];
        return queryClient.ensureQueryData({
            queryKey: cache,
            queryFn: ({ signal }) =>
                callGetCompetitionsCollections({
                    customQuery: customQueryString,
                    settings: { signal },
                }).then((res) => {
                    if (res?.ok && res.value) {
                        setCollectionsStatusesStore(res.value);
                        return res.value;
                    }
                }),

            retry: false,
        });
    };
};
